import {
    Button,
    DialogTrigger,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuOpenChangeData,
    MenuPopover,
    MenuProps,
    MenuTrigger,
    PositioningImperativeRef,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import {
    bundleIcon,
    CodeTextEditFilled,
    Comment20Filled,
    Comment20Regular,
    DataLine20Regular,
    Open20Regular,
    TextBulletListSquareEdit20Regular,
} from '@fluentui/react-icons';
import { FormFieldModel } from '@syncfusion/ej2-react-pdfviewer';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGetEntity } from '../../api/context/context';
import { useIsFeatureEnabled } from '../../api/features/features';
import { getSelectedEntityFromPath } from '../../contexts/formViewContext';
import { useFormView } from '../../contexts/useFormView';
import { createSelectedWhitePaperCell } from '../../data-types/selectedWhitePaperCell';
import { useDrillDown } from '../../hooks/useDrillDown';
import { useIsCollaborationUIEnabled } from '../../hooks/useIsCollaborationUIEnabled';
import { useServiceDiscovery } from '../../hooks/useServiceDiscovery';
import { useTaxReturnEventHandler } from '../../hooks/useTaxReturnEventHandler';
import {
    EntityMasterRecord,
    IDescriptiveDataProperty,
    IWhitePaperColumn,
    IWhitePaperMappedRowDictionary,
    IWhitePaperReportChanges,
    ViewFormResponseFormFields,
} from '../../model';
import { parseDate } from '../../utils/DateUtils';
import {
    CalcReviewEnhancedDescriptiveDataEnabled,
    CalcReviewFormCommentsEnabled,
    EntityManagementAppEnabled,
} from '../../utils/FeatureFlags.constants';
import {
    ELIMINATION_COLUMN_ID,
    getColumnFieldNameByIndex,
    isInputRow,
    resolveReportCellForFormField,
    SUBTOTAL_COLUMN_ID,
    TOTAL_COLUMN_ID,
} from '../../utils/ReportUtils';
import { TOTAL_ROW_NUMBER } from '../../utils/Table.constants';
import ConfirmationDialogV9 from '../dialog/ConfirmationDialogV9';
import EnterAmountsDialog, { IEnterAmountsDialogProps } from '../enter-amounts-dialog/EnterAmountsDialog';
import ReportDrillDown, { IReportDrillDownOnClose } from '../sidebar/ReportDrillDown';

const CommentIcon = bundleIcon(Comment20Filled, Comment20Regular);

export interface IPdfContextualMenuProps {
    open: boolean;
    isAmountField: boolean;
    IsDescriptiveDataField: boolean;
    isOverridableField?: boolean;
    setOpenAmountFieldMenu: (open: boolean) => void;
    setOpenDescriptiveDataFieldMenu: (open: boolean) => void;
    setOpenOverridableFieldMenu?: (open: boolean) => void;
    formFields?: ViewFormResponseFormFields;
    contextMenuTarget: FormFieldModel | undefined;
}

interface IColumnContext {
    entityId: string;
    contextId: number;
    entityModifiedDate: Date | undefined;
}

const useStyles = makeStyles({
    menuItemStyles: { minWidth: '248px', maxWidth: '500px', height: '32px', textWrapMode: 'nowrap' },
    menuPopoverStyles: { maxWidth: '500px' },
});

const PdfContextualMenu: React.FC<IPdfContextualMenuProps> = ({
    open,
    isAmountField,
    IsDescriptiveDataField,
    setOpenAmountFieldMenu,
    setOpenDescriptiveDataFieldMenu,
    setOpenOverridableFieldMenu,
    formFields,
    contextMenuTarget,
    isOverridableField,
}: IPdfContextualMenuProps) => {
    const [currentWhitePaperColumn, setCurrentWhitePaperColumn] = useState<IWhitePaperColumn | undefined>(undefined);
    const { setTraceCalculationData } = useDrillDown();
    const { currentReport, selectedEntity, currentWhitePaperReport, currentTOCItemState } = useFormView();
    // Set right before opening comments, checked (and cleared) by onOpenFieldMenuContextChange's
    // own restoreFocus call below — clicking the "Comments" item closes this Menu, which fires
    // that handler with data.open=false same as any other close, and it doesn't know comments
    // was the reason. Without this it steals focus back to the PDF field out from under the
    // comment editor just like the tail-call in openCommentsIfAllowed used to.
    const suppressNextFocusRestoreRef = useRef(false);
    const [isDrillDownVisible, { setTrue: showDrillDown, setFalse: hideDrillDown }] = useBoolean(false);
    const { setNewWarningMessage, clearWarningMessage } = useTaxReturnEventHandler();
    const [changeTrackingData, setChangeTrackingData] = useState<IWhitePaperReportChanges>({});
    const { t } = useTranslation();
    const [context, setContext] = useState<IColumnContext | null>(null);
    const [showEditPropertyUnavailableMessage, setShowEditPropertyUnavailableMessage] = useState<boolean>(false);
    const [editNotAvailableMessage, setEditNotAvailableMessage] = useState<string>('');
    const [enterAmountsDialogProps, setEnterAmountsDialogProps] = useState<IEnterAmountsDialogProps | null>(null);
    const { entityGeneralPropertiesUrl, entityObligationPropertiesUrl } = useServiceDiscovery();
    const { data: isEnterAmountsFromFormsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'EnterAmountsFromFormsEnabled',
    });
    const { data: isEntityManagementAppEnabled } = useIsFeatureEnabled({
        featureFlagName: EntityManagementAppEnabled,
    });
    const { data: isEnhancedDescriptiveDataEnabled } = useIsFeatureEnabled({
        featureFlagName: CalcReviewEnhancedDescriptiveDataEnabled,
    });
    const { data: isCalcReviewFormCommentsEnabled } = useIsFeatureEnabled({
        featureFlagName: CalcReviewFormCommentsEnabled,
    });
    const isCollaborationUIEnabled = useIsCollaborationUIEnabled();
    const navigate = useNavigate();
    const positioningRef = useRef<PositioningImperativeRef>(null);
    const { menuItemStyles, menuPopoverStyles } = useStyles();
    const searchParams = new URLSearchParams(location.search);
    const entityUrl = getSelectedEntityFromPath(searchParams);
    const selectedColumn = currentWhitePaperReport?.columns?.[selectedEntity?.index || entityUrl?.index || 0];
    const { refetch: getEntity } = useGetEntity({ entityKey: selectedColumn?.entityKey }, { query: { enabled: false, staleTime: 0 } });

    useEffect(() => {
        if (contextMenuTarget?.id) {
            positioningRef.current?.setTarget(document.getElementById(contextMenuTarget.id));
        }
        // A new right-click target means a fresh open→close cycle is starting - clear any
        // suppression left over from a previous one so a legitimate dismiss (e.g. clicking away
        // without selecting anything) still restores focus normally.
        suppressNextFocusRestoreRef.current = false;
    }, [contextMenuTarget]);

    useEffect(() => {
        if (selectedColumn?.entityKey) {
            getEntity().then((entity) => {
                if (entity.data) setContextAndWarnIfEntityModified(entity.data, selectedColumn);
            });
        }
    }, [selectedColumn]);

    const warnIfEntityModified = (entityModifiedDate: Date, executedDate: Date) => {
        if (entityModifiedDate > executedDate) {
            setNewWarningMessage(true, t('recalculateNeeded').toString());
        } else if (!currentWhitePaperReport?.reCalculationRequired) {
            clearWarningMessage();
        }
    };

    const setContextAndWarnIfEntityModified = (entity: EntityMasterRecord, selectedColumn: IWhitePaperColumn) => {
        if (entity.entityId) {
            const entityModifiedDate = parseDate(entity.entityModified);
            const executedDate = parseDate(currentReport?.executed);

            setContext({
                entityId: entity.entityId,
                contextId: selectedColumn.contextId || 0,
                entityModifiedDate: entityModifiedDate,
            });

            if (entityModifiedDate && executedDate) {
                warnIfEntityModified(entityModifiedDate, executedDate);
            }
        }
    };

    const onOpenFieldMenuContextChange: MenuProps['onOpenChange'] = (_, data: MenuOpenChangeData) => {
        // Fluent's Menu fires onOpenChange more than once per item click (e.g. once as
        // 'menuItemClick', again shortly after) - suppressNextFocusRestoreRef stays true for the
        // rest of this open→close cycle (cleared on the next right-click, see the effect above)
        // so it suppresses all of them, not just the first.
        if (!data.open && !isDrillDownVisible && !enterAmountsDialogProps && !suppressNextFocusRestoreRef.current) {
            restoreFocus(contextMenuTarget?.id);
        }

        if (data.type === 'clickOutside') {
            setOpenAmountFieldMenu(false);
            setOpenDescriptiveDataFieldMenu(false);
            setOpenOverridableFieldMenu?.(false);
            return;
        }

        if (isAmountField) {
            setOpenAmountFieldMenu(data.open);
        } else if (IsDescriptiveDataField) {
            setOpenDescriptiveDataFieldMenu(data.open);
        } else {
            setOpenOverridableFieldMenu?.(data.open);
        }
    };

    const openDrillDownIfAllowed = () => {
        const extractedData = extractDataForContextMenuTarget();
        if (extractedData && currentWhitePaperReport) {
            const { currentColumn, row } = extractedData;
            setCurrentWhitePaperColumn(currentColumn);
            if (row) {
                setTraceCalculationData([
                    { displayRowNumber: TOTAL_ROW_NUMBER },
                    createSelectedWhitePaperCell(
                        row!,
                        getColumnFieldNameByIndex(currentColumn?.originalColumnIndex || 0, currentWhitePaperReport)
                    ),
                ]);

                showDrillDown();
            }
        }
    };

    // Restore focus - requestAnimationFrame ensures this runs after DOM updates are complete but before the next visual update
    const restoreFocus = (elementId: string | undefined) => {
        if (elementId) {
            requestAnimationFrame(() => {
                document.getElementById(elementId)?.focus();
            });
        }
    };

    const closeDrillDown = (changeTrackingData: IReportDrillDownOnClose) => {
        const { amountsChangedOnServer, reportChanges } = changeTrackingData;
        hideDrillDown();
        setNewWarningMessage(amountsChangedOnServer, t('recalculateNeeded').toString());
        setChangeTrackingData(reportChanges);
        restoreFocus(contextMenuTarget?.id);
    };

    const extractDataForContextMenuTarget = () => {
        const resolved = resolveReportCellForFormField(
            contextMenuTarget?.name,
            formFields,
            currentWhitePaperReport ?? undefined,
            selectedEntity?.index
        );
        if (!resolved) {
            return null;
        }

        const descriptiveDataColumnIndex: number | undefined =
            [TOTAL_COLUMN_ID, ELIMINATION_COLUMN_ID, SUBTOTAL_COLUMN_ID].indexOf(resolved.currentColumn?.originalColumnId ?? 0) === -1
                ? resolved.currentColumn?.originalColumnId
                : resolved.currentColumn?.columnIndex;
        const descriptiveDataProperties =
            currentWhitePaperReport?.descriptiveData?.[descriptiveDataColumnIndex ?? -1]?.[resolved.matchField.row ?? -1] ?? undefined;

        return { ...resolved, descriptiveDataProperties };
    };

    const openEnterAmountsIfAllowed = () => {
        const extractedData = extractDataForContextMenuTarget();

        if (extractedData) {
            const { matchField, currentColumn, row } = extractedData;
            if (row && currentReport && currentColumn && matchField) {
                const rowDescription = isInputRow(row)
                    ? `Row ${row.description || matchField.row}, ${currentColumn.entityCode} ${currentColumn.entityName}`
                    : '';
                setEnterAmountsDialogProps({
                    adjustmentCode: '',
                    case: currentReport.case || '',
                    jurisdiction: currentReport.jurisdiction || '',
                    entity: currentColumn.entityCode || '',
                    period: currentReport.year?.toString() || '',
                    location: currentReport.location || '',
                    drillDownRowNumber: matchField.row || 0,
                    definingRowNumber: matchField.row,
                    columnId: currentColumn.originalColumnId || 0,
                    changeTrackingData: changeTrackingData,
                    taxReturnKey: currentReport.id || 0,
                    rowDescription: rowDescription,
                    onClose: (amountsChangedOnServer: boolean, updatedChangeTrackingData: IWhitePaperReportChanges) => {
                        if (amountsChangedOnServer) {
                            setNewWarningMessage(true, t('recalculateNeeded').toString());
                            setChangeTrackingData(updatedChangeTrackingData);
                        }
                        setEnterAmountsDialogProps(null);
                        restoreFocus(contextMenuTarget?.id);
                    },
                    isInputRow: isInputRow(row),
                });
            }
        }

        restoreFocus('enterAmountsDialog');
    };

    const openCommentsIfAllowed = () => {
        const extractedData = extractDataForContextMenuTarget();

        if (extractedData) {
            const { currentColumn, row } = extractedData;
            // A field can resolve to a row without a displayRowNumber; navigating anyway builds a
            // /cell/<column>/undefined/comments URL that 404s.
            if (row?.displayRowNumber && currentColumn?.fieldName && currentReport?.id) {
                // Also stops onOpenFieldMenuContextChange's own restoreFocus call, fired when
                // this menu item click closes the Menu, from stealing focus right back.
                suppressNextFocusRestoreRef.current = true;
                // Keep the /forms/:tocItemId prefix so the form stays displayed underneath the
                // panel — formViewContext's showForm is derived from the pathname starting with
                // /forms, so navigating to the bare /cell/... route would fall back to the report
                // tab. Navigation (rather than opening the panel via local state, matching the
                // report grid) is deliberate here.
                const formSegment = currentTOCItemState?.id ? `/forms/${encodeURIComponent(currentTOCItemState.id)}` : '';
                navigate({
                    pathname: `/view/${currentReport.id}${formSegment}/cell/${encodeURIComponent(currentColumn.fieldName)}/${
                        row.displayRowNumber
                    }/comments`,
                    search: location.search,
                });
                // Deliberately no restoreFocus() here, unlike the other menu actions below: those
                // close a transient dialog and should hand focus back to the field that opened it,
                // but comments open a panel the user is about to type into - restoring focus to
                // the field (on the next animation frame, right as the panel/editor mounts and
                // tries to focus itself) was yanking focus back out of the editor.
            }
        }
    };

    const openLegacyPropertyEdit = () => {
        if (!context?.entityId) {
            return;
        }
        if (isEntityManagementAppEnabled) {
            // New entity management app URL format
            const periodParam = currentReport?.year ? `&period=${currentReport.year}` : '';
            window.open(`${entityGeneralPropertiesUrl}?id=${context.entityId}${periodParam}`, '_blank');
        } else {
            if (context?.contextId) {
                window.open(`${entityGeneralPropertiesUrl}?eid=${context?.entityId}&contextId=${context?.contextId}`, '_blank');
            }
        }
    };

    const openPropertyEdit = (discriptiveDataPropertyIndex: number) => {
        const extractedData = extractDataForContextMenuTarget();

        if (!extractedData) {
            return;
        }

        const { matchField, currentColumn, descriptiveDataProperties } = extractedData;

        if (
            !(
                currentReport &&
                currentColumn &&
                matchField &&
                descriptiveDataProperties &&
                descriptiveDataProperties.length > discriptiveDataPropertyIndex
            )
        ) {
            return;
        }

        const discriptiveDataProperty: IDescriptiveDataProperty = descriptiveDataProperties[discriptiveDataPropertyIndex];

        if (discriptiveDataProperty.message) {
            setEditNotAvailableMessage(discriptiveDataProperty.message);
            setShowEditPropertyUnavailableMessage(true);
            return;
        }

        if (discriptiveDataProperty.isObligation) {
            const entityParam: string = discriptiveDataProperty.entityId!;
            const caseParam: string = currentReport.case!;
            const jurisdictionParam: string = discriptiveDataProperty.jurisdictionId!;
            const periodParam: string = currentReport.year!.toString();
            const obligationParam: string = discriptiveDataProperty.obligationTypeId!;
            const obligationPropertiesUrl: string = entityObligationPropertiesUrl!
                .replace('<CASE>', caseParam)
                .replace('<ENTITY>', entityParam)
                .replace('<JURISDICTION>', jurisdictionParam)
                .replace('<PERIOD>', periodParam)
                .replace('<OBLIGATION>', obligationParam);

            window.open(obligationPropertiesUrl, '_blank');
        } else {
            if (isEntityManagementAppEnabled) {
                const entityParam = `id=${discriptiveDataProperty.entityId}`;
                const periodParam = `period=${currentReport.year}`;
                const propertyParam = `property=${discriptiveDataProperty.name}`;

                window.open(`${entityGeneralPropertiesUrl}?${entityParam}&${periodParam}&${propertyParam}`, '_blank');
            } else if (context?.contextId) {
                window.open(
                    `${entityGeneralPropertiesUrl}?eid=${discriptiveDataProperty.entityId}&contextId=${context.contextId}`,
                    '_blank'
                );
            }
        }
    };

    const generateEditDataContextMenuItem = () => {
        const { matchField, currentColumn, descriptiveDataProperties } = extractDataForContextMenuTarget()!;

        if (
            !(currentReport && currentColumn && matchField) ||
            (isEnhancedDescriptiveDataEnabled && descriptiveDataProperties?.length === 0)
        ) {
            return null;
        }

        return isEnhancedDescriptiveDataEnabled ? (
            descriptiveDataProperties!.length === 1 ? (
                <MenuItem
                    icon={<CodeTextEditFilled />}
                    className={menuItemStyles}
                    secondaryContent={<Open20Regular />}
                    onClick={() => openPropertyEdit(0)}
                >
                    {t('editData')}
                </MenuItem>
            ) : (
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <MenuItem icon={<CodeTextEditFilled />} className={menuItemStyles}>
                            {t('editData')}
                        </MenuItem>
                    </MenuTrigger>
                    <MenuPopover className={menuPopoverStyles}>
                        <MenuList>{generateEditMenuItems(descriptiveDataProperties!)}</MenuList>
                    </MenuPopover>
                </Menu>
            )
        ) : (
            <MenuItem
                icon={<CodeTextEditFilled />}
                className={menuItemStyles}
                secondaryContent={<Open20Regular />}
                onClick={openLegacyPropertyEdit}
            >
                {t('editData')}
            </MenuItem>
        );
    };

    const generateEditMenuItems = (descriptiveDataProperties: IDescriptiveDataProperty[]) => {
        return descriptiveDataProperties.map((entityProperty: IDescriptiveDataProperty, index: number) => (
            <MenuItem
                key={`${entityProperty.entityId ?? ''}-${entityProperty.description ?? ''}-${index}`}
                className={menuItemStyles}
                secondaryContent={<Open20Regular />}
                onClick={() => openPropertyEdit(index)}
            >
                {entityProperty.description}
            </MenuItem>
        ));
    };

    return (
        <>
            {enterAmountsDialogProps && <EnterAmountsDialog {...enterAmountsDialogProps} />}

            {isDrillDownVisible && currentReport && (
                <ReportDrillDown
                    onClose={closeDrillDown}
                    report={currentReport}
                    showPanel={isDrillDownVisible}
                    whitePaperReportMap={currentWhitePaperReport?.rowsMap as IWhitePaperMappedRowDictionary}
                    whitePaperReportColumn={currentWhitePaperColumn}
                    changeTrackingData={changeTrackingData}
                    isMultiColumnWhitePaperReport={false}
                />
            )}

            {showEditPropertyUnavailableMessage && (
                <ConfirmationDialogV9
                    modalType='modal'
                    title={t('editUnavailable')}
                    content={editNotAvailableMessage}
                    buttons={
                        <>
                            <DialogTrigger>
                                <Button style={{ visibility: 'hidden' }} disabled={true}></Button>
                            </DialogTrigger>
                            <Button
                                appearance='primary'
                                aria-label={t('ok').toString()}
                                onClick={() => setShowEditPropertyUnavailableMessage(false)}
                                data-analytics-button-name='confirmation_dialog_ok'
                            >
                                {t('ok')}
                            </Button>
                        </>
                    }
                />
            )}

            <Menu hasIcons open={open} onOpenChange={onOpenFieldMenuContextChange} positioning={{ positioningRef }}>
                <MenuPopover className={menuPopoverStyles}>
                    <MenuList>
                        {isAmountField && (
                            <>
                                <MenuItem
                                    onClick={openDrillDownIfAllowed}
                                    className={menuItemStyles}
                                    icon={<DataLine20Regular />}
                                    data-testid='drillDownMenuItem'
                                >
                                    {t('reportDrillDown')}
                                </MenuItem>
                                {isEnterAmountsFromFormsEnabled && (
                                    <MenuItem
                                        onClick={openEnterAmountsIfAllowed}
                                        className={menuItemStyles}
                                        icon={<TextBulletListSquareEdit20Regular />}
                                        data-testid='enterAmountsMenuItem'
                                    >
                                        {t('enterAmountsContextMenuName')}
                                    </MenuItem>
                                )}
                            </>
                        )}
                        {IsDescriptiveDataField && generateEditDataContextMenuItem()}
                        {isOverridableField && isCollaborationUIEnabled && isCalcReviewFormCommentsEnabled && (
                            <MenuItem
                                onClick={openCommentsIfAllowed}
                                className={menuItemStyles}
                                icon={<CommentIcon />}
                                data-testid='formFieldCommentsMenuItem'
                            >
                                {t('viewComments')}
                            </MenuItem>
                        )}
                    </MenuList>
                </MenuPopover>
            </Menu>
        </>
    );
};

export default PdfContextualMenu;
